import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import AppCheckbox from 'components/App/AppCheckbox'
import AppFlex from 'components/App/AppFlex'
import AppText from 'components/App/AppText'
import AppIcon from 'components/App/AppIcon'
import AppInput from 'components/App/AppInput'
import AppTag from 'components/App/AppTag'

import helper from './helper'

export interface AppDropdownOptionType {
  value: string | number | null
  label: string
  disabled?: boolean
  hidden?: boolean
  search?: string[]
}

export interface AppDropdownProps {
  value: AppDropdownOptionType['value'] | AppDropdownOptionType['value'][]
  options: AppDropdownOptionType[]
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  multiple?: boolean
  searchable?: boolean
  error?: string
  danger?: boolean
  small?: boolean
  large?: boolean
  minWidth?: number
  full?: boolean
  width?: string | number | Array<string | number>
  fullWidth?: boolean
  halfWidth?: boolean
  height?: string | number | Array<string | number>
  fullHeight?: boolean
  halfHeight?: boolean
  flex?: number | Array<number>
  style?: React.CSSProperties
  containerStyle?: React.CSSProperties
  onChange: (value: AppDropdownOptionType['value'] | AppDropdownOptionType['value'][]) => void
}

export default function AppDropdown(props: AppDropdownProps) {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)

  const buttonRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonResizeObs = useRef<ResizeObserver | null>(null)
  const dropdownResizeObs = useRef<ResizeObserver | null>(null)
  const rafRef = useRef<number | null>(null)

  const classes = [
    ...helper.init(props),
  ]

  const styles = {
    ...(props.style ?? {}),
  }

  const dropdownClasses = [
    ...helper.initDropdown(props),
  ]

  const containerProps = {
    full: props.full,
    width: props.width,
    fullWidth: props.fullWidth ?? true,
    halfWidth: props.halfWidth,
    height: props.height,
    fullHeight: props.fullHeight,
    halfHeight: props.halfHeight,
    flex: props.flex,
  }

  const wrapperClasses = [
    ...helper.initWrapper(props),
    helper.size(props),
    helper.disabled(props),
    helper.error(props),
  ]

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (buttonRef.current && dropdownRef.current && !buttonRef.current.contains(e.target as Node) && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const buttonEl = buttonRef.current
    const dropdownEl = dropdownRef.current

    const onReposition = () => updateDropdownPosition()

    if (open) {
      updateDropdownPosition()
      window.addEventListener('resize', onReposition)
      window.addEventListener('scroll', onReposition, true)

      if (buttonEl && !buttonResizeObs.current) {
        const obs = new ResizeObserver(() => scheduleUpdate())
        buttonResizeObs.current = obs
        obs.observe(buttonEl)
      }

      if (dropdownEl && !dropdownResizeObs.current) {
        const obs = new ResizeObserver(() => scheduleUpdate())
        dropdownResizeObs.current = obs
        obs.observe(dropdownEl)
      }

      rafRef.current = requestAnimationFrame(() => updateDropdownPosition())
    } else {
      setDropdownStyle({})

      if (buttonResizeObs.current && buttonEl) {
        buttonResizeObs.current.unobserve(buttonEl)
        buttonResizeObs.current.disconnect()
        buttonResizeObs.current = null
      }

      if (dropdownResizeObs.current && dropdownEl) {
        dropdownResizeObs.current.unobserve(dropdownEl)
        dropdownResizeObs.current.disconnect()
        dropdownResizeObs.current = null
      }
    }

    return () => {
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }

      if (buttonResizeObs.current && buttonEl) {
        buttonResizeObs.current.unobserve(buttonEl)
        buttonResizeObs.current.disconnect()
        buttonResizeObs.current = null
      }

      if (dropdownResizeObs.current && dropdownEl) {
        dropdownResizeObs.current.unobserve(dropdownEl)
        dropdownResizeObs.current.disconnect()
        dropdownResizeObs.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.minWidth])

  const scheduleUpdate = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }

    rafRef.current = requestAnimationFrame(() => {
      updateDropdownPosition()
    })
  }

  const selectedOptions: AppDropdownOptionType[] = useMemo(() => {
    if (!props.multiple) {
      const match = props.options.find(option => option.value === props.value)
      if (match) {
        return [match]
      }

      return []
    }

    const vals = Array.isArray(props.value) ? props.value : []
    return vals.map(val => props.options.find(opt => opt.value === val)).filter(Boolean) as AppDropdownOptionType[];
  }, [props.value, props.options, props.multiple])

  const filteredOptions: AppDropdownOptionType[] = useMemo(() => {
    const q = search.trim().toLowerCase()
    return props.options.filter(option => {
      if (option.hidden) {
        return false
      }

      if (!props.searchable || q === '') {
        return true
      }

      const inLabel = option.label.toLowerCase().includes(q)
      const inSearch = Array.isArray(option.search) && option.search.some(s => s.toLowerCase().includes(q))
      return inLabel || inSearch
    })
  }, [props.options, search, props.searchable])

  useEffect(() => {
    if (!open) {
      return
    }

    scheduleUpdate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filteredOptions.length, selectedOptions.length, open])

  const handleOpenToggle = () => {
    if (!props.disabled && !props.readOnly) {
      setOpen(prev => !prev)
    }
  }

  const updateDropdownPosition = () => {
    if (!open || !buttonRef.current || !dropdownRef.current) {
      return
    }

    const buttonRect = buttonRef.current.getBoundingClientRect()

    const dropdownEl = dropdownRef.current
    const naturalContentHeight = dropdownEl.scrollHeight

    const below = window.innerHeight - buttonRect.bottom
    const above = buttonRect.top

    const minWidth = props.minWidth
    let width = Math.max(buttonRect.width, minWidth ?? buttonRect.width)

    dropdownEl.querySelectorAll<HTMLElement>('.app-dropdown__option').forEach(option => {
      width = Math.max(width, option.scrollWidth)
    })

    const pageWidth = document.documentElement.clientWidth
    const spaceToRight = pageWidth - (buttonRect.left + window.scrollX)
    const maxAllowed = spaceToRight - 10

    width = Math.min(width, maxAllowed)

    let top: number
    let left: number
    let desiredHeight: number

    const canOpenUp = above > below
    if (canOpenUp) {
      const availableUp = above - 10
      desiredHeight = Math.min(naturalContentHeight, availableUp)
      top = buttonRect.top + window.scrollY - desiredHeight
    } else {
      const availableDown = below - 10
      desiredHeight = Math.min(naturalContentHeight, availableDown)
      top = buttonRect.bottom + window.scrollY
    }

    if (top < 0) {
      top = 0
    }

    left = buttonRect.left + window.scrollX
    const pageWidthCheck = document.documentElement.clientWidth
    if (left + width > pageWidthCheck) {
      left = buttonRect.right - width + window.scrollX
    }

    setDropdownStyle({
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      maxHeight: `${desiredHeight}px`,
    })
  }

  const handleSelectToggle = (value: AppDropdownOptionType['value']) => {
    if (props.disabled || props.readOnly) {
      return
    }

    if (props.multiple) {
      const vals = Array.isArray(props.value) ? [...props.value] : []
      const idx = vals.indexOf(value)
      if (idx >= 0) {
        vals.splice(idx, 1)
      } else {
        vals.push(value)
      }

      props.onChange(vals)
    } else {
      props.onChange(value)
      setOpen(false)
    }
  }

  const handleRemove = (value: AppDropdownOptionType['value']) => {
    if (!props.multiple) {
      return
    }

    const values = Array.isArray(props.value) ? [...props.value] : []
    props.onChange(values.filter(v => v !== value))
  }

  const isSelectable = (opt: AppDropdownOptionType | undefined) => {
    return !!opt && !opt.hidden && !opt.disabled
  }

  const moveHighlight = (dir: 1 | -1) => {
    if (!open) {
      return
    }

    if (!filteredOptions.length) {
      return
    }

    let next = highlightedIndex
    const len = filteredOptions.length

    if (next === -1) {
      next = 0
      while (next < len && !isSelectable(filteredOptions[next])) {
        next++
      }

      if (next >= len) {
        return
      }

      setHighlightedIndex(next)
      ensureVisible(next)
      return
    }

    for (let i = 0; i < len; i++) {
      next = (next + dir + len) % len
      if (isSelectable(filteredOptions[next])) {
        setHighlightedIndex(next)
        ensureVisible(next)
        break
      }
    }
  }

  const ensureVisible = (index: number) => {
    if (!dropdownRef.current) {
      return
    }

    const el = dropdownRef.current.querySelector<HTMLElement>(`.app-dropdown__option[data-index="${index}"]`)
    if (el) {
      el.scrollIntoView({ block: 'nearest' })
    }
  }

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if (!open) {
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveHighlight(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveHighlight(-1)
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        e.preventDefault()
        const opt = filteredOptions[highlightedIndex]
        if (isSelectable(opt)) {
          handleSelectToggle(opt.value)
        }
      }
    }
  }

  return (
    <AppFlex column gap={1} style={props.containerStyle} {...containerProps}>
      <AppFlex aCenter className={cn(wrapperClasses)} ref={buttonRef}>
        <AppFlex fullWidth onClick={handleOpenToggle}>
          <AppFlex fullWidth aCenter gap={2} className={cn(classes)} style={styles}>
            <AppFlex fullHeight flex={1} style={{minWidth: 0}}>
              {props.multiple && selectedOptions.length > 0 ? (
                <AppFlex fullWidth wrap gap={1}>
                  {selectedOptions.map(option => (
                    <AppTag
                      key={option.value}
                      value={option.value}
                      label={option.label}
                      highlight
                      onRemove={handleRemove}
                    />
                  ))}
                </AppFlex>
              ) : (
                <AppFlex gap={2} fullWidth aCenter>
                  <AppText muted={selectedOptions.length === 0}>
                    {selectedOptions.length > 0 ? selectedOptions[0].label : (props.placeholder ?? t('Select...'))}
                  </AppText>
                </AppFlex>
              )}
            </AppFlex>

            <AppFlex aCenter>
              <AppIcon icon="chevron-down" size={16} rotate={open} />
            </AppFlex>
          </AppFlex>
        </AppFlex>

        {open && createPortal(
          <div ref={dropdownRef} className={cn(dropdownClasses)} style={dropdownStyle}>
            {props.searchable && (
              <AppFlex padding={2}>
                <AppInput
                  value={search}
                  placeholder={t('Search...')}
                  onChange={value => setSearch(value)}
                />
              </AppFlex>
            )}

            {filteredOptions.length === 0 ? (
              <AppFlex center padding={4}>
                <AppText muted center>{t('Nothing found')}</AppText>
              </AppFlex>
            ) : (
              filteredOptions.map((option, index) => (
                <AppFlex
                  key={option.value}
                  gap={2}
                  className={cn('app-dropdown__option', helper.option(props, option.value, option.disabled ?? false, index === highlightedIndex))}
                  onClick={() => {
                    if (!option.disabled) {
                      setHighlightedIndex(index)
                      handleSelectToggle(option.value)
                    }
                  }}
                >
                  {props.multiple && (
                    <AppCheckbox
                      value={Array.isArray(props.value) ? (props.value as any[]).includes(option.value) : false}
                      checked={Array.isArray(props.value) ? (props.value as any[]).includes(option.value) : false}
                      disabled={option.disabled}
                      notActive
                      onChange={() => {}}
                    />
                  )}

                  <AppText nowrap highlight={props.multiple && Array.isArray(props.value) && (props.value as any[]).includes(option.value) || props.value === option.value}>
                    {option.label}
                  </AppText>
                </AppFlex>
              ))
            )}
          </div>,
          document.body
        )}
      </AppFlex>

      {props.error && props.error !== '' ? (
        <AppFlex>
          <AppText small danger>{props.error}</AppText>
        </AppFlex>
      ) : null}
    </AppFlex>
  )
}